import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, Cloud, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ThemeToggle from "@/components/theme/ThemeToggle";

const services = [
  { name: "CloudOps as a Service", href: "/services#cloudops", desc: "24/7 managed cloud operations" },
  { name: "DevOps as a Service", href: "/services#devops", desc: "CI/CD, IaC, GitOps automation" },
  { name: "Cloud Migration", href: "/services#migration", desc: "Zero-downtime lift and modernize" },
  { name: "Project Implementation", href: "/services#implementation", desc: "Greenfield to production" },
  { name: "Custom AI Agents", href: "/services#ai-agents", desc: "Deploy LLMs on your infra" },
  { name: "Kubernetes Modernization", href: "/services#kubernetes", desc: "EKS, AKS, GKE + GitOps" },
];

const navLinks = [
  { name: "Services", href: "/services", hasDropdown: true },
  { name: "Case Studies", href: "/case-studies" },
  { name: "About", href: "/about" },
  { name: "Blog", href: "/blog" },
  { name: "Contact", href: "/contact" },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-strong border-b border-border/60" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="container mx-auto flex items-center justify-between h-16 px-4 lg:px-8">
        <Link to="/" className="flex items-center gap-2 font-heading font-semibold text-lg group">
          <div className="relative">
            <Cloud className="h-6 w-6 text-primary transition-transform group-hover:scale-110" />
            <div className="absolute inset-0 blur-md bg-primary/40 -z-10 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <span className="tracking-tight">
            Cloud<span className="text-gradient">ease</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
          {navLinks.map((link) => (
            <div
              key={link.name}
              className="relative"
              onMouseEnter={() => link.hasDropdown && setServicesOpen(true)}
              onMouseLeave={() => link.hasDropdown && setServicesOpen(false)}
            >
              {link.hasDropdown ? (
                <button
                  className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    location.pathname === link.href ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {link.name}
                  <ChevronDown className={`h-3 w-3 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                </button>
              ) : (
                <Link
                  to={link.href}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    location.pathname === link.href ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {link.name}
                </Link>
              )}

              {link.hasDropdown && (
                <AnimatePresence>
                  {servicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[420px]"
                    >
                      <div className="glass-strong rounded-2xl shadow-elevated p-2 grid grid-cols-1">
                        {services.map((s) => (
                          <Link
                            key={s.name}
                            to={s.href}
                            className="group flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-muted transition-colors"
                          >
                            <div className="h-8 w-8 rounded-lg bg-brand flex items-center justify-center shrink-0 opacity-90">
                              <ArrowRight className="h-3.5 w-3.5 text-white" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">{s.name}</p>
                              <p className="text-xs text-muted-foreground">{s.desc}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-2">
          <ThemeToggle />
          <a href={import.meta.env.VITE_CALENDLY_URL} target="_blank" rel="noopener noreferrer">
            <Button size="sm" className="rounded-full">
              Book a call
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </a>
        </div>

        <div className="flex lg:hidden items-center gap-2">
          <ThemeToggle />
          <button
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            className="p-2 rounded-lg glass"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass-strong border-t border-border/60 overflow-hidden"
          >
            <div className="container mx-auto px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-3 text-sm font-medium rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-3">
                <a href={import.meta.env.VITE_CALENDLY_URL} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)}>
                  <Button className="w-full rounded-full" size="lg">Book a call</Button>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
